export const ROW_LABEL_PROP = '__rowLabel';

const DEFAULT_COLUMN_SIZE = 150;
const ROW_LABEL_SIZE = 200;

export function formatCurrency(value) {
  if (value === null || value === undefined || value === '') return '';
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numericValue)) return value;

  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericValue);
}

export function normalizeGridValue(value, formatter = null) {
  if (formatter) return formatter(value);
  if (value === null || value === undefined) return '';
  return value;
}

function renderClampedText(h, value, className) {
  const text = normalizeGridValue(value);

  return h(
    'span',
    {
      class: className,
      title: String(text)
    },
    text
  );
}

export function makeGridColumn({
  prop,
  name,
  size = DEFAULT_COLUMN_SIZE,
  kind = 'value',
  formatter = null,
  pinned = false
}) {
  return {
    prop,
    name,
    size,
    pin: pinned ? 'colPinStart' : undefined,
    readonly: true,
    sortable: false,
    columnTemplate: h => renderClampedText(h, name, 'crisis-report-header-text'),
    cellTemplate: (h, { model }) => h(
      'span',
      {
        class: 'crisis-report-grid-value',
        title: String(normalizeGridValue(model[prop], formatter))
      },
      normalizeGridValue(model[prop], formatter)
    ),
    cellProperties: () => ({
      class: {
        'crisis-grid-row-header': kind === 'row-header',
        'crisis-grid-total-cell': kind === 'total',
        'crisis-grid-summary-cell': kind === 'summary'
      }
    }),
    columnProperties: () => ({
      class: {
        'crisis-grid-total-header': kind === 'total',
        'crisis-grid-summary-header': kind === 'summary'
      }
    })
  };
}

export function makeRowHeaderColumn(name = '') {
  return makeGridColumn({
    prop: ROW_LABEL_PROP,
    name,
    size: ROW_LABEL_SIZE,
    kind: 'row-header',
    pinned: true
  });
}

export function makeColumnGroup(name, children) {
  return {
    name,
    columnTemplate: h => renderClampedText(h, name, 'crisis-report-header-text'),
    columnProperties: group => ({
      style: {
        top: `calc(${group.level || 0} * var(--crisis-report-header-row-height))`,
        height: 'var(--crisis-report-header-row-height)'
      }
    }),
    children
  };
}

export function makeSafeProp(...parts) {
  return parts
    .map(part => String(part ?? '')
      .trim()
      .replace(/[^a-zA-Z0-9_]/g, '_'))
    .filter(Boolean)
    .join('__');
}

export function onRegionSelectionChange(region, checked) {
  const isChecked = typeof checked === 'boolean' ? checked : region.checked;
  region.checked = isChecked;
  region.cities.forEach(city => {
    city.checked = isChecked;
  });
}

export function hasAnyRegionOrCitySelection(selected) {
  return selected.some(region =>
    region.checked ||
    region.cities.some(city => city.checked)
  );
}

export function getSelectedRegion(selected, regionName) {
  return selected.find(region => region.region === regionName);
}

export function shouldIncludeMunicipality({ selectedRegion, municipality, hasAnySelection }) {
  if (!selectedRegion) return false;
  if (!hasAnySelection) return true;
  if (selectedRegion.checked) return true;

  return selectedRegion.cities.some(city =>
    city.key === municipality &&
    city.checked
  );
}

export function getSelectedMunicipalityKeys(selectedRegion, municipalities, hasAnySelection) {
  if (!selectedRegion) return [];
  if (!hasAnySelection || selectedRegion.checked) return municipalities;

  return municipalities.filter(municipality =>
    selectedRegion.cities.some(city =>
      city.key === municipality &&
      city.checked
    )
  );
}
